"use client";

import React from "react";
import { Calendar, User, Clock, ArrowRight, Share2, Tag } from "lucide-react";
import { PostsProvider, usePosts, type Post } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";



function RelatedPostsConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div data-section-id="6720" className="grid grid-cols-1 gap-8 md:grid-cols-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-96 w-full animate-pulse rounded-xl bg-muted/50" />
        ))}
      </div>
    );
  }

  if (error || !posts || posts.length === 0) return null;

  return (
    <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
      {posts.map((post, i) => (
        <Card key={post.id} data-index={i} className="group overflow-hidden border-none bg-card shadow-md transition-all hover:-translate-y-1 hover:shadow-xl">
          <div className="relative aspect-[4/3] overflow-hidden">
            {post.featuredImage ? (
              <img
                src={post.featuredImage}
                alt={post.title}
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-muted text-muted-foreground">
                <span className="text-4xl font-serif opacity-20">Aİ</span>
              </div>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-60 transition-opacity group-hover:opacity-70" />
            <div className="absolute bottom-4 left-4 right-4">
              <span className="mb-2 inline-block rounded-full bg-secondary px-3 py-1 text-xs font-medium text-secondary-foreground">
                {post.categories?.[0]?.name || "Diş Sağlığı"}
              </span>
            </div>
          </div>
          <CardHeader className="pb-2">
            <CardTitle className="font-serif text-xl leading-tight text-foreground group-hover:text-primary">
              <Link to={post.link || "#"}>{post.title}</Link>
            </CardTitle>
            <CardDescription className="line-clamp-2 text-sm text-muted-foreground">
              {post.excerpt ? <span dangerouslySetInnerHTML={{ __html: post.excerpt }} /> : "Klinik uygulamalarımız ve ağız sağlığı hakkında güncel bilgiler."}
            </CardDescription>
          </CardHeader>
          <CardFooter className="pt-4">
            <Link to={post.link || "#"} className="group/link flex items-center text-sm font-medium text-primary">
              Makaleyi Oku <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover/link:translate-x-1" />
            </Link>
          </CardFooter>
        </Card>
      ))}
    </div>
  );
}

function RelatedPostsSection() {
  
  
  const relatedQuery = new WP_Query({
    per_page: 3,
    ignore_sticky: true,
  });

  return (
    <div className="mt-24 bg-secondary/20 py-16">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="mb-12 flex items-end justify-between">
          <div>
            <h2 className="font-serif text-3xl font-bold text-foreground md:text-4xl">Bilgi Köşesi</h2>
            <p className="mt-2 text-muted-foreground">Ağız sağlığı ve estetik diş hekimliği üzerine güncel paylaşımlar.</p>
          </div>
          <Link to="/BlogPage">
            <Button variant="outline" className="hidden md:flex">Tüm Yazıları Gör</Button>
          </Link>
        </div>
        
        <PostsProvider wp_query={{ per_page: 3, ignore_sticky: true }}>
          <RelatedPostsConsumer />
        </PostsProvider>

        <div className="mt-8 flex justify-center md:hidden">
          <Link to="/BlogPage">
            <Button variant="outline">Tüm Yazıları Gör</Button>
          </Link>
        </div>
      </div>
    </div>
  );
}



function SinglePostConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl px-6 py-24">
        <div className="space-y-8 animate-pulse">
          <div className="h-8 w-3/4 rounded bg-muted" />
          <div className="h-96 w-full rounded-xl bg-muted" />
          <div className="space-y-4">
            <div className="h-4 w-full rounded bg-muted" />
            <div className="h-4 w-full rounded bg-muted" />
            <div className="h-4 w-2/3 rounded bg-muted" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center px-6 text-center">
        <div className="max-w-md space-y-4">
          <h2 className="font-serif text-2xl font-bold text-destructive">Yazı Yüklenemedi</h2>
          <p className="text-muted-foreground">Aradığınız içeriğe ulaşılamadı. Lütfen daha sonra tekrar deneyiniz.</p>
          <Link to="/HomePage">
            <Button>Ana Sayfaya Dön</Button>
          </Link>
        </div>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center px-6 text-center">
        <div className="max-w-md space-y-4">
          <h2 className="font-serif text-2xl font-bold">İçerik Bulunmuyor</h2>
          <p className="text-muted-foreground">İlgili makale şu anda mevcut değil.</p>
          <Link to="/HomePage">
            <Button>Ana Sayfaya Dön</Button>
          </Link>
        </div>
      </div>
    );
  }

  const post = posts[0];
  const authorName = post.author?.name || "Dt. Ayşegül İnneci Uzun";
  const publishDate = post.date ? new Date(post.date).toLocaleDateString("tr-TR", { month: "long", day: "numeric", year: "numeric" }) : "";

  return (
    <article className="min-h-screen bg-background">
      {}
      <div className="relative w-full bg-muted/10 pb-12 pt-32 md:pt-40">
        <div className="mx-auto max-w-4xl px-6 text-center">
          {post.categories && post.categories.length > 0 && (
            <div className="mb-6 flex justify-center gap-2">
              {post.categories.map((cat, i) => (
                <span key={i} data-index={i} className="rounded-full bg-primary/10 px-4 py-1.5 text-sm font-medium text-primary">
                  {cat.name}
                </span>
              ))}
            </div>
          )}
          
          <h1 className="font-serif text-4xl font-bold leading-tight text-foreground md:text-5xl lg:text-6xl">
            {post.title}
          </h1>

          <div className="mt-8 flex flex-wrap items-center justify-center gap-6 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <Avatar className="h-8 w-8 border border-primary/20">
                <AvatarImage src={post.author?.avatar?.["96"] || ""} alt={authorName} />
                <AvatarFallback><User className="h-4 w-4" /></AvatarFallback>
              </Avatar>
              <span className="font-medium text-foreground">{authorName}</span>
            </div>
            {publishDate && (
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <span>{publishDate}</span>
              </div>
            )}
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              <span>4 dk okuma</span>
            </div>
          </div>
        </div>
      </div>

      {}
      {post.featuredImage && (
        <div className="mx-auto -mt-8 max-w-5xl px-4 md:px-6 lg:px-8">
          <div className="relative aspect-video w-full overflow-hidden rounded-2xl shadow-2xl">
            <img
              src={post.featuredImage}
              alt={post.title}
              className="h-full w-full object-cover"
            />
          </div>
        </div>
      )}

      {}
      <div className="mx-auto max-w-3xl px-6 py-16 md:py-24">
        <div 
          className="prose prose-lg prose-stone dark:prose-invert max-w-none 
            prose-headings:font-serif prose-headings:font-bold prose-headings:text-foreground 
            prose-p:font-sans prose-p:text-muted-foreground prose-p:leading-relaxed
            prose-a:text-primary prose-a:no-underline hover:prose-a:underline
            prose-img:rounded-xl prose-img:shadow-lg
            prose-blockquote:border-l-primary prose-blockquote:bg-muted/30 prose-blockquote:px-6 prose-blockquote:py-2 prose-blockquote:not-italic"
          dangerouslySetInnerHTML={{ __html: post.content || "" }}
        />

        {}
        <div className="mt-16 flex flex-col gap-8 border-y border-border py-8 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-wrap items-center gap-3">
            <Tag className="h-5 w-5 text-muted-foreground" />
            <span className="font-medium text-foreground">Etiketler:</span>
            {post.tags && post.tags.length > 0 ? (
              post.tags.map((tag, i) => (
                <Link key={i} data-index={i} to={tag.link || "#"} className="text-sm text-muted-foreground hover:text-primary hover:underline">
                  #{tag.name}
                </Link>
              ))
            ) : (
              <span className="text-sm text-muted-foreground">Kayseri Diş Hekimi, Gülüş Tasarımı</span>
            )}
          </div>
          
          <div className="flex items-center gap-4">
            <span className="font-medium text-foreground">Paylaş:</span>
            <Button variant="outline" size="icon" className="rounded-full hover:bg-primary hover:text-primary-foreground">
              <Share2 className="h-4 w-4" />
            </Button>
            {}
            <Button variant="outline" size="icon" className="rounded-full hover:bg-[#1877F2] hover:text-white hover:border-[#1877F2]">
              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path fillRule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clipRule="evenodd" /></svg>
            </Button>
            <Button variant="outline" size="icon" className="rounded-full hover:bg-[#1DA1F2] hover:text-white hover:border-[#1DA1F2]">
              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" /></svg>
            </Button>
          </div>
        </div>

        {}
        <div className="mt-12 flex flex-col gap-6 rounded-2xl bg-secondary/30 p-8 md:flex-row md:items-center">
          <Avatar className="h-20 w-20 border-2 border-background shadow-sm">
            <AvatarImage src={post.author?.avatar?.["96"] || ""} alt={authorName} />
            <AvatarFallback className="text-xl"><User /></AvatarFallback>
          </Avatar>
          <div className="flex-1 space-y-2 text-center md:text-left">
            <h3 className="font-serif text-xl font-bold text-foreground">Dt. {authorName} Hakkında</h3>
            <p className="text-sm text-muted-foreground">
              Estetik diş hekimliği ve modern tedavi yöntemleri ile Kayseri'de sağlıklı gülüşler tasarlıyoruz. 
              Hasta odaklı yaklaşımımızla konforlu bir klinik deneyimi sunuyoruz.
            </p>
            <Link to={post.author?.link || "#"} className="inline-block text-sm font-medium text-primary hover:underline">
              Tüm yazılarını görüntüle
            </Link>
          </div>
        </div>
      </div>

      {}
      <RelatedPostsSection />
    </article>
  );
}

export default function SinglePostTemplateSection() {
  
  
  const currentWPQueryParams = (window as any).wvcClient?.getCurrentWPQueryParams() || {};
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section id="single-post-template-section" className="bg-background">
      <PostsProvider wp_query={wp_query}>
        <SinglePostConsumer />
      </PostsProvider>
    </section>
  );
}